import { z } from 'zod';

export const DemoMemoryPresetsSchema = z
  .union([
    z.literal('manager'),
    z.literal('office'),
    z.literal('developer'),
    z.literal('designer'),
    z.literal('seeing_machines'),
    z.literal('pwc'),
  ])
  .default('manager');
export type DemoMemoryPreset = z.infer<typeof DemoMemoryPresetsSchema>;
const demoMemoryPresetLabels: Record<DemoMemoryPreset, string> = {
  manager: 'Manager',
  office: 'Office',
  developer: 'Developer',
  designer: 'Designer',
  seeing_machines: 'Custom for Seeing Machines',
  pwc: 'Custom for PwC',
};
export const DemoMemoryPresets: Array<{ label: string; value: DemoMemoryPreset }> = Object.entries(
  demoMemoryPresetLabels
).map(([value, label]) => ({
  label,
  value: value as DemoMemoryPreset,
}));

const DemoMemoryCalendarToolSchema = z
  .union([z.literal('google_calendar'), z.literal('microsoft_outlook_calendar')])
  .default('google_calendar');
export type DemoMemoryCalendarTool = z.infer<typeof DemoMemoryCalendarToolSchema>;
const demoMemoryCalendarToolLabels: Record<DemoMemoryCalendarTool, string> = {
  google_calendar: 'Google Calendar',
  microsoft_outlook_calendar: 'O365 Calendar',
};
export const DemoMemoryCalendarTools: Array<{
  label: string;
  value: DemoMemoryCalendarTool;
}> = Object.entries(demoMemoryCalendarToolLabels).map(([value, label]) => ({
  label,
  value: value as DemoMemoryCalendarTool,
}));

const DemoMemoryMeetingToolSchema = z
  .union([z.literal('google_meet'), z.literal('microsoft_teams'), z.literal('zoom')])
  .default('zoom');
export type DemoMemoryMeetingTool = z.infer<typeof DemoMemoryMeetingToolSchema>;
const demoMemoryMeetingToolLabels: Record<DemoMemoryMeetingTool, string> = {
  google_meet: 'Google Meet',
  microsoft_teams: 'Microsoft Teams',
  zoom: 'Zoom',
};
export const DemoMemoryMeetingTools: Array<{
  label: string;
  value: DemoMemoryMeetingTool;
}> = Object.entries(demoMemoryMeetingToolLabels).map(([value, label]) => ({
  label,
  value: value as DemoMemoryMeetingTool,
}));

const DemoMemoryProjectManagementToolSchema = z
  .union([
    z.literal('clickup'),
    z.literal('monday'),
    z.literal('asana'),
    z.literal('jira'),
    z.literal('linear'),
  ])
  .default('linear');
export type DemoMemoryProjectManagementTool = z.infer<typeof DemoMemoryProjectManagementToolSchema>;
const demoMemoryProjectManagementToolLabels: Record<DemoMemoryProjectManagementTool, string> = {
  clickup: 'ClickUp',
  monday: 'Monday',
  asana: 'Asana',
  jira: 'Jira',
  linear: 'Linear',
};
export const DemoMemoryProjectManagementTools: Array<{
  label: string;
  value: DemoMemoryProjectManagementTool;
}> = Object.entries(demoMemoryProjectManagementToolLabels).map(([value, label]) => ({
  label,
  value: value as DemoMemoryProjectManagementTool,
}));

export const DemoMemorySettingsSchema = z.object({
  enabled: z.boolean().default(false),
  preset: DemoMemoryPresetsSchema,
  calendarTool: DemoMemoryCalendarToolSchema,
  meetingTool: DemoMemoryMeetingToolSchema,
  projectManagementTool: DemoMemoryProjectManagementToolSchema,
});
export type DemoMemorySettings = z.infer<typeof DemoMemorySettingsSchema>;
export const DefaultDemoMemorySettings: DemoMemorySettings = DemoMemorySettingsSchema.parse({});

export interface DemoMemoryBlock {
  note: string;
  aiNotes: {
    concise: string;
    detailed: string;
  };
  from: string;
  to: string;
}
